import { X, Camera, Users, MessageCircle, SkipForward, Shield } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useEffect } from 'react';

const HowItWorksModal = ({ isOpen, onClose }) => {
  // Handle ESC key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  const steps = [
    {
      icon: Camera,
      title: 'Allow Camera & Microphone',
      description: 'Grant permission to start chatting.'
    },
    {
      icon: Users,
      title: 'Click Start',
      description: "We'll instantly connect you with someone random."
    },
    {
      icon: MessageCircle,
      title: 'Meet New People',
      description: 'Talk using live audio, and reveal yourself when you choose.'
    },
    {
      icon: SkipForward,
      title: 'Skip Anytime',
      description: "Click Next whenever you'd like a new conversation."
    },
    {
      icon: Shield,
      title: 'Stay Safe',
      description: "Never share passwords, OTPs, banking details or anything you're uncomfortable sharing."
    }
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
          />
          
          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: 'spring', duration: 0.5 }}
            className="fixed inset-0 flex items-center justify-center z-50 p-4 pointer-events-none"
          >
            <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto pointer-events-auto">
              {/* Header */}
              <div className="sticky top-0 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 px-8 py-6 flex items-center justify-between">
                <h2 className="text-3xl font-black">How Toonah Works</h2>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              {/* Steps */}
              <div className="px-8 py-8 space-y-8">
                {steps.map((step, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-start space-x-4"
                  >
                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                      <step.icon className="w-6 h-6 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold mb-1">{index + 1}. {step.title}</h3>
                      <p className="text-slate-600 dark:text-slate-400">{step.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
              
              {/* Footer */}
              <div className="border-t border-slate-200 dark:border-slate-800 px-8 py-6 text-center">
                <p className="text-lg font-semibold text-blue-600">Have fun meeting new people! 🎉</p>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default HowItWorksModal;
